import type { Dir } from './grid';
import { FNV_OFFSET, digestTick, fold, toHex8 } from './hash';
import type { ContentPack } from './pack';
import type { EngineRules } from './rules';
import { type SessionInfo, createCtx, createState } from './state';
import { step } from './step';
import type { ChoiceLog, EngineCtx, EngineState, InputLog, RunArgs, RunResult } from './types';

export interface Runner {
  readonly ctx: EngineCtx;
  state: EngineState;
  checksum: number;
  /** choice 는 부활 페이즈에서만 소비된다(그 외 틱에는 무시). */
  tickOnce(dir: Dir | null, choice?: number | null): void;
  result(): RunResult;
}

export function summarize(s: EngineState, checksum: number, runWordCount: number): RunResult {
  let wrong = 0;
  let solved = 0;
  let lastSolved = -1;
  for (const rec of s.records) {
    wrong += rec.wrongEats;
    if (rec.outcome === 'solved') {
      solved++;
      lastSolved = rec.endTick;
    }
  }
  // sealed 는 endReason 을 보지 않는다 — 8문제 소진과 8문제 해결은 다르다
  const sealed = solved === runWordCount;
  const rv = s.revive;
  return {
    engineVersion: s.engineVersion,
    gridVersion: s.gridVersion,
    seed: s.seed,
    sessionSeed: s.sessionSeed,
    runIndex: s.runIndex,
    packVersion: s.packVersion,
    packHash: s.packHash,
    engineRulesHash: s.engineRulesHash,
    ticks: s.tick,
    nominalMs: s.nominalMs,
    endReason: s.endReason,
    sealed,
    solved,
    lastSolveTick: lastSolved,
    sealTicks: sealed ? lastSolved : -1,
    peakLength: s.peakLength,
    moveTicks: s.moveTicks,
    autoGrown: s.ledger.byTime,
    wrongEats: wrong,
    spawnDegraded: s.spawnDegraded,
    spawnRelaxed: s.spawnRelaxed,
    ledger: s.ledger,
    reviveOffered: rv.offered,
    reviveUsed: rv.used,
    reviveResult: rv.result,
    reviveQuestionId: rv.questionId,
    reviveAnswerTicks: rv.answerTick >= 0 ? rv.answerTick - rv.openTick : -1,
    reviveTicks: s.reviveTicks,
    records: s.records,
    checksum: toHex8(checksum),
  };
}

export function createRunner(
  seed: string,
  pack: ContentPack,
  rules: EngineRules,
  session?: SessionInfo,
): Runner {
  const ctx = createCtx(pack, rules);
  const r: Runner = {
    ctx,
    state: createState(seed, ctx, session),
    checksum: FNV_OFFSET,
    tickOnce(dir, choice = null) {
      if (r.state.phase === 'over') return;
      r.state = step(r.state, dir, ctx, choice);
      r.checksum = fold(r.checksum, digestTick(r.state));
    },
    result() {
      return summarize(r.state, r.checksum, ctx.rules.runWordCount);
    },
  };
  r.checksum = fold(r.checksum, digestTick(r.state)); // 초기 상태 포함
  return r;
}

/** 틱 오름차순·틱당 최대 1개 로그를 앞으로만 읽는 리더. 입력 로그와 선택 로그가 같은 규약을 쓴다. */
function createLogReader<E extends { readonly t: number }, V>(
  log: readonly E[],
  pick: (e: E) => V,
  name: string,
): (tick: number) => V | null {
  for (let k = 1; k < log.length; k++) {
    const cur = log[k];
    const prev = log[k - 1];
    if (cur === undefined || prev === undefined || cur.t <= prev.t) {
      throw new Error(`${name}: 틱 오름차순·중복 없음 위반`);
    }
  }
  let i = 0;
  let last = -1;
  return (tick) => {
    if (tick <= last) throw new Error(`${name}: 앞으로만 읽는다`);
    last = tick;
    while (i < log.length && (log[i] as { t: number }).t < tick) i++;
    const e = log[i];
    if (e !== undefined && e.t === tick) {
      i++;
      return pick(e);
    }
    return null;
  };
}

export function createInputReader(log: InputLog): (tick: number) => Dir | null {
  return createLogReader(log, (e) => e.d, 'inputLog');
}

export function createChoiceReader(log: ChoiceLog): (tick: number) => number | null {
  return createLogReader(log, (e) => e.c, 'choiceLog');
}

export function run(args: RunArgs): RunResult {
  const cap = Math.min(args.maxTicks ?? args.rules.runMaxTicks, args.rules.runMaxTicks);
  const runner = createRunner(args.seed, args.pack, args.rules, args.session);
  const read = createInputReader(args.inputLog);
  const readChoice = createChoiceReader(args.choiceLog ?? []);
  let guard = 0;
  while (runner.state.phase !== 'over' && runner.state.tick < cap) {
    if (++guard > cap + 1) throw new Error('run: 전이하지 않는 상태');
    const t = runner.state.tick + 1;
    runner.tickOnce(read(t), readChoice(t));
    args.onTick?.(runner.state);
  }
  return runner.result();
}
